import React from 'react';

interface Priority {
  ShortTitle: string;
  Description: string;
}

interface ImmediatePrioritiesSectionProps {
  priorities?: Priority[];
}

interface Strategy {
  id: string;
  title: string;
  description: string;
  icon: string;
  color: string;
  position: { x: number; y: number };
}

const ImmediatePrioritiesSection: React.FC<ImmediatePrioritiesSectionProps> = ({ priorities = [] }) => {
  // Define colors and icons for dynamic assignment
  const colors = ['#ef4444', '#f97316', '#eab308', '#84cc16', '#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899'];
  const icons = ['⚡', '🎯', '📋', '🚀', '💡', '⭐', '🔥', '✨'];
  
  const isMobile = window.innerWidth <= 768;
  
  // Generate strategies from priorities prop
  const strategies: Strategy[] = priorities.map((priority, index) => {
    const angle = (index * 360) / priorities.length;
    const radius = 190;
    const x = Math.cos((angle - 90) * Math.PI / 180) * radius;
    const y = Math.sin((angle - 90) * Math.PI / 180) * radius;
    
    return {
      id: `priority-${index}`,
      title: priority.ShortTitle,
      description: priority.Description,
      icon: icons[index % icons.length],
      color: colors[index % colors.length],
      position: { x: Math.round(x), y: Math.round(y) }
    };
  });
  
  // If no priorities provided, show a placeholder message
  if (priorities.length === 0) {
    return (
      <div style={{
        background: '#fff',
        borderRadius: '12px',
        padding: '2rem',
        //marginBottom: '1.5rem',
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        breakInside: 'avoid',
        textAlign: 'center',
        color: '#6b7280'
      }}>
        <div style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>⚡</div>
        <h3 style={{ margin: '0 0 0.5rem 0', color: '#374151' }}>Immediate Priorities</h3>
        <p style={{ margin: 0 }}>No immediate priorities available</p>
      </div>
    );
  }

  // Mobile layout - vertical cards
  if (isMobile) {
    return (
      <div style={{
        background: '#fff',
        borderRadius: '12px',
        padding: '1.5rem',
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        borderLeft: '4px solid #667eea'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '1.5rem',
          gap: '0.75rem'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.25rem'
          }}>
            ⚡
          </div>
          <h3 style={{
            margin: 0,
            fontSize: '1.25rem',
            fontWeight: '700',
            color: '#1a202c'
          }}>
            Immediate Priorities
          </h3>
        </div>
        
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem'
        }}>
          {strategies.map((strategy, index) => (
            <div
              key={strategy.id}
              style={{
                padding: '1rem',
                borderRadius: '8px',
                background: '#f8fafc',
                border: `2px solid ${strategy.color}`,
                borderLeft: `6px solid ${strategy.color}`
              }}
            >
              <div style={{
                fontWeight: '700',
                color: strategy.color,
                marginBottom: '0.5rem',
                fontSize: '1rem'
              }}>
                {strategy.icon} {strategy.title}
              </div>
              <div style={{
                color: '#4a5568',
                fontSize: '0.875rem',
                lineHeight: '1.5'
              }}>
                {strategy.description}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Desktop layout - circular
  return (
    <div style={{
      padding: '2rem',
      marginBottom: '1.5rem',
      breakInside: 'avoid'
    }}>
      <div style={{
        position: 'relative',
        width: '100%',
        height: '450px',
        maxHeight: '80vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // overflow: 'hidden'
      }}>
        {/* Central Circle */}
        <div style={{
          position: 'absolute',
          width: '180px',
          height: '180px',
          borderRadius: '50%',
          background: 'linear-gradient(115deg, rgb(34, 34, 69) 25%, rgb(-77, 182, 159) 162%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: '1.25rem',
          fontWeight: '700',
          textAlign: 'center',
          lineHeight: '1.3',
          zIndex: 10,
          boxShadow: '0 8px 32px rgba(0,0,0,0.15)'
        }}>
          IMMEDIATE<br />PRIORITIES
        </div>

        {/* Strategy Cards */}
        {strategies.map((strategy) => (
          <div
            key={strategy.id}
            style={{
              position: 'absolute',
              width: '140px',
              height: '120px',
              padding: '1rem',
              borderRadius: '12px',
              background: 'white',
              boxShadow: `0 4px 20px ${strategy.color}30`,
              border: `3px solid ${strategy.color}`,
              textAlign: 'center',
              fontSize: '0.75rem',
              lineHeight: '1.4',
              left: `calc(50% + ${strategy.position.x}px)`,
              top: `calc(50% + ${strategy.position.y}px)`,
              transform: 'translate(-50%, -50%)'
            }}
          >
            {/* Title */}
            <div style={{
              fontWeight: '700',
              color: strategy.color,              
              fontSize: '1rem'
            }}>
              {strategy.title}
            </div>
            
            {/* Description */}
            <div style={{
              color: '#4a5568',
              fontSize: '0.66rem'
            }}>
              {strategy.description}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ImmediatePrioritiesSection;
